import { Box, Grid, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { FormDatePicker, GridCardContent } from "components/atoms";
import { AppTable, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DownloadHelper, TableHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import {
  IForkliftPerformance,
  IGetForkliftPerformance,
} from "model/ForkLiftPerformance";
import moment, { Moment } from "moment";
import { useState } from "react";
import { ReportApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import { FIRST_PAGE, CURRENT_PAGE_SIZE, EXPORT_FILE_NAME } from "utils/Const";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { LoadingButton } from "@mui/lab";
import { Download } from "@mui/icons-material";
import { DownloadActions } from "redux/actions";
import { IDownloadExcelRequest, IPaginateApiResponse } from "model";

const ForkliftProductivityReport = () => {
  const dispatch = useAppDispatch();

  const [request, setRequest] = useState<IGetForkliftPerformance>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
  });

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber: pageNumber,
    });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const { loading, result } = usePaginationRequest<
    IGetForkliftPerformance,
    IForkliftPerformance[]
  >(request, ReportApiService.getForkliftPerformance);

  const downloadForkliftPerformance = async () => {
    const params: IDownloadExcelRequest = {
      ...request,
      fromDate: request.fromDate,
      toDate: request.toDate,
      fileName: EXPORT_FILE_NAME.forkliftPerformanceReport,
      pageSize: (result as IPaginateApiResponse<IForkliftPerformance[]>)
        .totalRecords,
      pageNumber: FIRST_PAGE,
    };
    dispatch(DownloadActions.downloadForkliftPerformanceReport.request(params));
  };

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <DetailHeader
        request={request}
        onSubmit={setRequest}
        onDownload={downloadForkliftPerformance}
      />
      <TableSection
        loading={loading}
        result={result}
        request={request}
        onSearch={handleSearch}
        onChangePage={handleChangePage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

const DetailHeader = (props: {
  request: IGetForkliftPerformance;
  onSubmit(values: IGetForkliftPerformance): void;
  onDownload(): void;
}) => {
  const { request, onSubmit, onDownload } = props;
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const initialValues = {
    fromDate: moment(request.fromDate, "MM-DD-YYYY"),
    toDate: moment(request.toDate, "MM-DD-YYYY"),
  };
  const formik = useFormik<{
    fromDate: Moment;
    toDate: Moment;
  }>({
    initialValues,
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      const params = {
        pageNumber: 1,
        pageSize: request.pageSize,
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      };
      onSubmit(params);
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.forkliftPerformance)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            startIcon={<Download />}
            loading={downloading}
            variant="contained"
            onClick={onDownload}
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const TableSection = (props: {
  loading: boolean;
  result: IPaginateApiResponse<IForkliftPerformance[]>;
  request: IGetForkliftPerformance;
  onChangePage(pageNumber: number): void;
  onPageSizeChange(pageSize: number): void;
  onSearch(searchText: string): void;
}) => {
  const { loading, result, onChangePage, onPageSizeChange, onSearch } = props;

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "strDate",
      headerName: I18nTranslator(translations.report.date),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "forklift",
      headerName: I18nTranslator(translations.report.forklift),
      size: "medium",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "palletcode",
      headerName: I18nTranslator(translations.report.palletCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.report.productCode),
      size: "small",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.report.productName),
      size: "full",
    }),
    TableHelper.createCell({
      field: "productCodeOld",
      headerName: I18nTranslator(translations.report.productCodeOld),
      size: "small",
    }),
    TableHelper.createCell({
      field: "quantity",
      headerName: I18nTranslator(translations.report.quantity),
      align: "right",
      size: "small",
    }),
    TableHelper.createCell({
      field: "fromZoneName",
      headerName: I18nTranslator(translations.report.fromZone),
      size: "medium",
    }),
    TableHelper.createCell({
      field: "fromLocationName",
      headerName: I18nTranslator(translations.report.fromLocation),
      size: "medium",
    }),
    TableHelper.createCell({
      field: "toZoneName",
      headerName: I18nTranslator(translations.report.toZone),
      size: "medium",
    }),
    TableHelper.createCell({
      field: "toLocationName",
      headerName: I18nTranslator(translations.report.toLocation),
      size: "medium",
    }),
    TableHelper.createCell({
      field: "driverReceivedDate",
      headerName: I18nTranslator(translations.report.startTimeScanPalletCode),
      size: "medium",
    }),
    TableHelper.createCell({
      field: "locationPutDate",
      headerName: I18nTranslator(translations.report.endTimeScanLocationCode),
      size: "medium",
    }),
    TableHelper.createCell({
      field: "minuteToAction",
      headerName: I18nTranslator(translations.report.timeAction),
      size: "small",
      align: "right",
    }),
  ];

  return (
    <AppTable<IForkliftPerformance>
      loading={loading}
      data={result}
      columns={COLUMNS}
      onSearch={onSearch}
      searchPlaceholder={"Tìm kiếm: Mã pallet"}
      onChangePage={onChangePage}
      hideHeader
      removeActionColumn
      defaultHeaderTable={170}
      onPageSizeChange={onPageSizeChange}
    />
  );
};

export default ForkliftProductivityReport;
